import { aFechaISO, sumarDias } from "./fechas";
import { periodoResumen, type VistaResumen } from "./resumen";

/** Panel de Inicio nuevo (45, auditoría 30/10/2026): lo que devuelve `nevora_panel`. */
export type CifrasPanel = {
  llamadas: number;
  contestadas: number;
  gestionados: number;
  interesados: number;
  citas: number;
  realizadas: number;
  no_shows: number;
  propuestas: number;
  clientes: number;
  /** null si quien mira no es Jan o Robert. */
  cobrado: number | null;
};

export type PasoEmbudo =
  | "entrados"
  | "gestionados"
  | "contactados"
  | "interesados"
  | "cita"
  | "realizada"
  | "propuesta"
  | "cliente";

export type DatosPanel = {
  periodo: {
    desde: string;
    hasta: string;
    anterior_desde: string;
    anterior_hasta: string;
  };
  kpis: { actual: CifrasPanel; anterior: CifrasPanel };
  embudo: { paso: PasoEmbudo; n: number }[];
  serie: {
    dia: string;
    llamadas: number;
    contestadas: number;
    citas: number;
  }[];
  ranking: {
    sale_id: number;
    nombre: string;
    llamadas: number;
    contestadas: number;
    citas: number;
    realizadas: number;
    no_shows: number;
    clientes: number;
  }[];
  consultores: {
    sale_id: number;
    nombre: string;
    hechas: number;
    no_shows: number;
    clientes: number;
  }[];
  origenes: {
    origen: string;
    entrados: number;
    contactados: number;
    citas: number;
    clientes: number;
  }[];
  motivos: { motivo: string; n: number }[];
  dinero: {
    firmado: number;
    cobrado: number;
    pendiente_cobro: number;
    comisiones_pendientes: number;
  } | null;
};

export type TipoAlerta =
  | "consultoria_sin_resultado"
  | "cita_sin_confirmar_48h"
  | "sin_intento_24h"
  | "paso_vencido"
  | "info_cliente_vencida"
  | "aceptada_sin_cobro"
  | "sin_asignar";

export type AlertaPanel = { tipo: TipoAlerta; n: number; negocios: number[] };

/** Orden de gravedad de las alertas (lo que más urge, arriba). */
export const ORDEN_ALERTAS: TipoAlerta[] = [
  "consultoria_sin_resultado",
  "cita_sin_confirmar_48h",
  "sin_intento_24h",
  "paso_vencido",
  "info_cliente_vencida",
  "aceptada_sin_cobro",
  "sin_asignar",
];

export const ordenarAlertas = (alertas: AlertaPanel[]) =>
  [...alertas]
    .filter((a) => a.n > 0)
    .sort(
      (a, b) => ORDEN_ALERTAS.indexOf(a.tipo) - ORDEN_ALERTAS.indexOf(b.tipo),
    );

/** Periodos del panel: los del Resumen más el trimestre. */
export type VistaPanel = VistaResumen | "trimestre";

/** Primer y último día (incluido) del periodo que contiene `ref`, como fechas AAAA-MM-DD. */
export const periodoPanel = (
  vista: VistaPanel,
  ref: Date,
): { desde: string; hasta: string } => {
  if (vista === "trimestre") {
    const t = Math.floor(ref.getMonth() / 3) * 3;
    return {
      desde: aFechaISO(new Date(ref.getFullYear(), t, 1)),
      hasta: aFechaISO(new Date(ref.getFullYear(), t + 3, 0)),
    };
  }
  const { desde, hasta } = periodoResumen(vista, ref);
  return { desde: aFechaISO(desde), hasta: aFechaISO(sumarDias(hasta, -1)) };
};

/** El periodo anterior (-1) o siguiente (+1). */
export const moverPeriodoPanel = (
  vista: VistaPanel,
  ref: Date,
  paso: 1 | -1,
) => {
  if (vista === "trimestre")
    return new Date(ref.getFullYear(), ref.getMonth() + 3 * paso, 1);
  if (vista === "mes")
    return new Date(ref.getFullYear(), ref.getMonth() + paso, 1);
  return sumarDias(ref, (vista === "dia" ? 1 : 7) * paso);
};

/**
 * Variación frente al periodo anterior, en %. null si no se puede comparar (antes, cero) o si
 * la base es tan pequeña que el % engaña (menos de `minimo`).
 */
export const variacion = (
  actual: number | null,
  anterior: number | null,
  minimo = 5,
): number | null => {
  if (actual == null || anterior == null || anterior === 0) return null;
  if (Math.max(actual, anterior) < minimo) return null;
  return Math.round(((actual - anterior) / anterior) * 100);
};

/** a / b en %, o null si b es 0. */
export const tasa = (a: number, b: number): number | null =>
  b > 0 ? Math.round((a / b) * 100) : null;

/**
 * El embudo con el % de cada paso sobre el anterior y el paso donde más se pierde (el de menor
 * paso de conversión, sin contar el primero y solo si el anterior tiene al menos 5).
 */
export const embudoConTasas = (embudo: DatosPanel["embudo"]) => {
  const pasos = embudo.map((p, i) => ({
    ...p,
    delAnterior: i === 0 ? null : tasa(p.n, embudo[i - 1].n),
    delTotal: tasa(p.n, embudo[0]?.n ?? 0),
  }));
  let peor: PasoEmbudo | null = null;
  let minimo = Number.POSITIVE_INFINITY;
  pasos.forEach((p, i) => {
    if (i === 0 || p.delAnterior == null || embudo[i - 1].n < 5) return;
    if (p.delAnterior < minimo) {
      minimo = p.delAnterior;
      peor = p.paso;
    }
  });
  return { pasos, peor: peor as PasoEmbudo | null };
};

/** ¿La base todavía no tiene la 45? Entonces el Inicio de siempre. */
export const esPanelNoDisponible = (e: unknown) =>
  /Could not find the function|nevora_panel.*does not exist|PGRST202/i.test(
    e instanceof Error ? e.message : String(e ?? ""),
  );
